"use client";

import React from "react";
import { motion } from "framer-motion";
import { Upload, FileText, SquareCheck as CheckSquare, Zap, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const workflowSteps = [{
  id: 1,
  title: "Submit Your File or Describe the Job",
  description: "Upload a DXF, AI, SVG, PDF, or EPS file with your cut profile and engraving artwork. Or describe the sign in the quote form — size, material, color, text, quantity, and mounting method. No file required for standard nameplate sizes.",
  icon: Upload
}, {
  id: 2,
  title: "Same-Day Quote",
  description: "Evermark returns a quote with material, cut dimensions, engraving details, unit price, and estimated ship date within one business day. Rush quoting available for time-sensitive projects.",
  icon: FileText
}, {
  id: 3,
  title: "Digital Proof Approval",
  description: "A digital proof showing the cut profile and engraving layout is sent before production starts. Approve via email or request changes. No acrylic is cut until the proof is signed off.",
  icon: CheckSquare
}, {
  id: 4,
  title: "Laser Cutting and Engraving",
  description: "Acrylic is cut and engraved at Evermark's US facility using CO₂ laser systems. Cut edges are clean-finished. Two-color laminate engraving reveals the contrasting core layer cleanly without additional finishing.",
  icon: Zap
}, {
  id: 5,
  title: "Packaged and Shipped",
  description: "Acrylic signs and cut parts are individually protected and packaged to prevent surface scratching during transit. Standard and expedited shipping available. Material data sheets included with industrial nameplate orders.",
  icon: Package
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    x: -20
  },
  show: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function ServiceWorkflow() {
  return <section id="serviceworkflow" className="bg-background py-24 px-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-16 text-center">
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            How Acrylic Sign and Cutting Orders Work
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            From file submission to final delivery, our process is built for speed, precision, and industrial compliance.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-100px"
      }} className="relative border-l-2 border-border ml-4 md:ml-8 space-y-12 pb-8">
          {workflowSteps.map((step, i) => {
          const Icon = step.icon;
          return <motion.div key={step.id} data-index={i} variants={itemVariants} className="relative pl-8 md:pl-16">
                {}
                <div className="absolute -left-[21px] top-0 flex size-10 items-center justify-center rounded-lg border-2 border-border bg-background text-primary shadow-sm">
                  <Icon className="size-5" />
                </div>

                <Card className="border-border/50 bg-card/50 shadow-md backdrop-blur-sm transition-colors hover:border-primary/30 hover:bg-card">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5">
                        Step {step.id}
                      </Badge>
                      <CardTitle className="font-default text-xl text-card-foreground">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="font-sans text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.6,
        duration: 0.5
      }} className="mt-16 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Button asChild size="lg" className="w-full sm:w-auto">
            <Link to="/ContactPage">Start Your Order</Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
            
          </Button>
        </motion.div>
      </div>
    </section>;
}